import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { useShipmentTaskFeed } from '#/entities/shipment'
import type { ShipmentTaskFeedItem } from '#/entities/shipment'
import {
  TaskStatusButton,
  useUpdateToolbarTask,
} from '#/features/shipment-toolbar'
import { HistoryRow, HistoryViewLink } from '#/shared/ui/history-row'

import { GroupedItems } from './grouped-items'

const COMPLETED_STATUS = 'completed'
const ARCHIVED_STATUS = 'archived'

function isActiveTask(task: ShipmentTaskFeedItem): boolean {
  return task.status !== COMPLETED_STATUS && task.status !== ARCHIVED_STATUS
}

function taskMeta(
  task: ShipmentTaskFeedItem,
  dueLabel: (when: string) => string,
): ReactNode[] {
  const meta: ReactNode[] = [task.timestamp]
  if (task.author) meta.push(task.author)
  if (task.date) {
    meta.push(
      dueLabel(
        task.startTime ? `${task.date} at ${task.startTime}` : task.date,
      ),
    )
  }
  if (task.assignees.length > 0) meta.push(task.assignees.join(', '))
  return meta
}

function ActiveTaskRow({
  task,
  pending,
  disabled,
  onMarkDone,
}: {
  task: ShipmentTaskFeedItem
  pending: boolean
  disabled: boolean
  onMarkDone: () => void
}) {
  const { t } = useTranslation()
  return (
    <HistoryRow
      title={task.title}
      wrapTitle
      titleLeading={
        <TaskStatusButton
          checked={false}
          onClick={onMarkDone}
          pending={pending}
          disabled={disabled}
          label={t('quoteDetails.tasks.markAsDone', { title: task.title })}
        />
      }
      meta={taskMeta(task, (when) => t('quoteDetails.tasks.due', { when }))}
      action={
        <HistoryViewLink
          variant="primary"
          onClick={onMarkDone}
          disabled={disabled}
        >
          {pending
            ? t('table.saving')
            : t('orderDetails.history.actions.markAsDone')}
        </HistoryViewLink>
      }
    />
  )
}

export function ActiveTasks({
  shipmentId,
}: {
  shipmentId: string | undefined
}) {
  const { t } = useTranslation()
  const { tasks, isLoading } = useShipmentTaskFeed(shipmentId ?? '', {
    enabled: Boolean(shipmentId),
  })
  const { markCompleted, isPending, pendingTaskId } = useUpdateToolbarTask()

  if (isLoading) return null
  const active = tasks.filter(isActiveTask)
  if (active.length === 0) return null

  return (
    <GroupedItems
      className="px-4 pb-4"
      title={t('detailSidebar.tasks.active')}
      toggleLabel={t('detailSidebar.tasks.toggleGroup')}
      items={active}
      getKey={(task) => task.id}
      renderItem={(task) => (
        <ActiveTaskRow
          task={task}
          pending={isPending && pendingTaskId === task.id}
          disabled={isPending}
          onMarkDone={() => markCompleted(task.id)}
        />
      )}
      moreLabel={(count) => t('detailSidebar.tasks.moreTasks', { count })}
      showMoreLabel={(count) => t('detailSidebar.tasks.showMore', { count })}
    />
  )
}
